import type React from "react"
import { normalizarId } from "../../hooks/useScope";
import { useTranslation } from "react-i18next"
import { useState, useEffect, useMemo, useCallback } from "react"
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Paper,
  Breadcrumbs,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from "@mui/material"
import { 
  Close as CloseIcon, 
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
} from "@mui/icons-material"
import EntityList from "../../components/EntityList"
import api from "../../services/api"
import HorseLoader from "../../components/loadingHorse"
import type { Stable, Tenant } from "../../interfaces/index"
import { useIsMobile, useIsTablet } from "../../hooks/useMediaQuery"
import { useCryptoNavigation, useAppNavigation } from "../../App"

interface Stall {
  stallId: number;
  stallName: string;
  stableId: number;
  horseName?: string;
  [key: string]: any;
}
interface StableListProps {
  tenant_id?: number
}

const StableList: React.FC<StableListProps> = ({ tenant_id }) => {
  const { t } = useTranslation()
  
  const { navigateToPage } = useCryptoNavigation()
  const { getParam } = useAppNavigation()
  
  const getUserTenantId = (): number | null => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return normalizarId(user?.tenantId, user?.tenant_id);
      }
    } catch (e) {
      console.error('Erro ao obter tenantId do usuário:', e);
    }
    return null;
  };
  
  const tenantIdFromParams = getParam('tenantId')
  const userTenantId = getUserTenantId();
  
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [showTenantSelector, setShowTenantSelector] = useState(false);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  
  // CORREÇÃO PRINCIPAL: Priorizar localStorage
  const finalTenantId = useMemo(() => {
    // 1. Se já selecionou manualmente nesta sessão, usar o selecionado
    if (selectedTenantId) {
      console.log('✅ Usando tenant selecionado manualmente:', selectedTenantId);
      return selectedTenantId;
    }
    
    // 2. Verificar localStorage PRIMEIRO (persistência entre navegações)
    const savedTenant = localStorage.getItem('selectedTenantId');
    if (savedTenant) {
      const tenantFromStorage = Number(savedTenant);
      console.log('✅ Tenant recuperado do localStorage:', tenantFromStorage);
      return tenantFromStorage;
    }
    
    // 3. Depois verificar props e params
    const id = tenant_id || Number(tenantIdFromParams) || userTenantId;
    
    console.log('🔍 Determinando tenantId:', {
      fromProp: tenant_id,
      fromParams: tenantIdFromParams,
      fromLocalStorage: savedTenant,
      fromUser: userTenantId,
      selected: selectedTenantId,
      final: id
    });
    
    return id;
  }, [tenant_id, tenantIdFromParams, userTenantId, selectedTenantId]);

  const theme = useTheme()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))

  const [selectedStable, setSelectedStable] = useState<Stable | null>(null)
  const [openDetailsDialog, setOpenDetailsDialog] = useState<boolean>(false)
  const [openStallsDialog, setOpenStallsDialog] = useState<boolean>(false)
  const [stalls, setStalls] = useState<Stall[]>([])
  const [loadingStalls, setLoadingStalls] = useState<boolean>(false)
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [loadingTenant, setLoadingTenant] = useState<boolean>(true)
  const [stallCounts, setStallCounts] = useState<{[key: number]: number}>({})
  const [stallCountsLoaded, setStallCountsLoaded] = useState<boolean>(false)

  console.log("📊 StableList state:", { 
    finalTenantId, 
    selectedTenantId,
    showTenantSelector,
    loadingTenant, 
    tenant: tenant?.tenantName, 
    stallCounts, 
    stallCountsLoaded 
  });

  // CORREÇÃO: Só mostrar seletor se realmente não tiver tenant em lugar nenhum
  useEffect(() => {
    const savedTenant = localStorage.getItem('selectedTenantId');
    
    // Se tem no localStorage, não mostrar seletor
    if (savedTenant) {
      console.log('✅ Tenant encontrado no localStorage, não mostrando seletor');
      setShowTenantSelector(false);
      return;
    }
    
    // Se tem finalTenantId, não mostrar seletor
    if (finalTenantId) {
      console.log('✅ finalTenantId existe, não mostrando seletor');
      setShowTenantSelector(false);
      return;
    }
    
    // Só mostrar seletor se realmente não tiver tenant
    if (!showTenantSelector) {
      console.log('⚠️ Nenhum tenantId disponível, mostrando seletor...');
      setShowTenantSelector(true);
      loadAvailableTenants();
    }
  }, [finalTenantId]);

  // Carregar lista de tenants disponíveis
  const loadAvailableTenants = async () => {
    setLoadingTenants(true);
    try {
      const response = await api.get('/tenant');
      console.log('📋 Tenants disponíveis:', response.data);
      
      let tenants: Tenant[] = [];
      
      // Extrair array de tenants
      if (Array.isArray(response.data)) {
        tenants = response.data;
      } else if (response.data?.tenants && Array.isArray(response.data.tenants)) {
        tenants = response.data.tenants;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        tenants = response.data.data;
      }
      
      setAvailableTenants(tenants);
    } catch (error) {
      console.error('❌ Erro ao carregar tenants:', error);
      setAvailableTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  };

  // CORREÇÃO: Salvar no localStorage ao confirmar
  const handleConfirmTenant = () => {
    if (selectedTenantId) {
      console.log('✅ Tenant selecionado:', selectedTenantId);
      
      // Salvar no localStorage para persistir
      localStorage.setItem('selectedTenantId', String(selectedTenantId));
      
      // Buscar e salvar o nome do tenant também
      const selectedTenant = availableTenants.find(t => t.tenantId === selectedTenantId);
      if (selectedTenant) {
        localStorage.setItem('selectedTenantName', selectedTenant.tenantName);
      }
      
      setShowTenantSelector(false);
      
      // Atualizar navegação com o tenant selecionado
      navigateToPage('estabulos', { mode: 'list', tenantId: String(selectedTenantId) });
    }
  };

  const handleNew = useCallback(() => {
    if (!finalTenantId) {
      setShowTenantSelector(true);
      return;
    }
    console.log("🚀 Navegando para novo estábulo");
    navigateToPage('estabulos', { mode: 'new', tenantId: String(finalTenantId) });
  }, [navigateToPage, finalTenantId]);

  const handleEdit = useCallback((stableId: number | string) => {
    if (!finalTenantId) {
      setShowTenantSelector(true);
      return;
    }
    console.log("✏️ Navegando para editar estábulo ID:", stableId);
    navigateToPage('estabulos', { mode: 'edit', id: String(stableId), tenantId: String(finalTenantId) });
  }, [navigateToPage, finalTenantId]);

  const handleBackToTenants = useCallback(() => {
    navigateToPage('proprietarios', { mode: 'list' })
  }, [navigateToPage]);

  // Carregar tenant apenas quando há finalTenantId
  useEffect(() => {
    if (!finalTenantId) {
      setLoadingTenant(false);
      return;
    }

    let mounted = true;
    console.log("🔍 StableList useEffect - buscando tenant");
    setLoadingTenant(true);

    const fetchTenant = async () => {
      try {
        const response = await api.get(`/tenant/${finalTenantId}`)
        
        if (!mounted) return;

        let tenantData = null

        if (response.data && response.data.tenant) {
          tenantData = response.data.tenant
        } else if (response.data && typeof response.data === "object") {
          if (response.data.tenantId && response.data.tenantName) {
            tenantData = response.data
          }
        }

        console.log("✅ Tenant carregado:", tenantData?.tenantName);
        setTenant(tenantData)
      } catch (error) {
        console.error("❌ Erro ao carregar tenant:", error)
      } finally {
        if (mounted) {
          setLoadingTenant(false)
        }
      }
    }

    fetchTenant()

    return () => {
      mounted = false;
    }
  }, [finalTenantId])

  // Carregar contagem de baias apenas quando há finalTenantId
  useEffect(() => {
    if (!finalTenantId) {
      setStallCountsLoaded(true);
      return;
    }

    let mounted = true;
    console.log("🏠 StableList useEffect - buscando contagem de baias");

    const fetchStallCounts = async () => {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      try {
        const response = await api.get(`/stall/${finalTenantId}`);
        console.log("Resposta completa das baias:", response);
        
        if (!mounted) return;
        
        let allStalls: Stall[] = [];
        
        if (Array.isArray(response.data)) {
          allStalls = response.data;
        } else if (response.data && response.data.stalls && Array.isArray(response.data.stalls)) {
          allStalls = response.data.stalls;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          allStalls = response.data.data;
        } else if (response.data && typeof response.data === "object") {
          for (const [key, value] of Object.entries(response.data)) {
            if (Array.isArray(value) && value.length > 0) {
              console.log(`Encontrado array de baias em response.data.${key}`);
              allStalls = value as Stall[];
              break;
            }
          }
        }
        
        console.log(`Total de baias encontradas: ${allStalls.length}`);
        
        const counts: {[key: number]: number} = {};
        allStalls.forEach((stall: Stall) => {
          if (stall.stableId) {
            counts[stall.stableId] = (counts[stall.stableId] || 0) + 1;
          }
        });
        
        console.log("✅ Contagem de baias calculada:", counts);
        setStallCounts(counts);
        setStallCountsLoaded(true);
      } catch (error) {
        console.error("❌ Erro ao carregar contagem de baias:", error)
        setStallCountsLoaded(true);
      }
    }

    fetchStallCounts()

    return () => {
      mounted = false;
    }
  }, [finalTenantId])

  const handleShowDetails = useCallback((stable: Stable): void => {
    setSelectedStable(stable)
    setOpenDetailsDialog(true)
  }, []);

  const handleShowStalls = useCallback(async (stable: Stable, e?: React.MouseEvent): Promise<void> => {
    if (e) {
      e.stopPropagation();
    }
    
    if (!finalTenantId) return;
    
    console.log("🏠 Carregando baias do estábulo:", stable.stableName);
    setSelectedStable(stable);
    setOpenStallsDialog(true);
    setLoadingStalls(true);
    
    try {
      const response = await api.get(`/stall/${finalTenantId}`);
      console.log("Resposta das baias:", response.data);
      
      let allStalls: Stall[] = [];
      
      if (Array.isArray(response.data)) {
        allStalls = response.data;
      } else if (response.data && response.data.stalls) {
        allStalls = response.data.stalls;
      } else if (response.data && typeof response.data === "object") {
        const values = Object.values(response.data);
        const arrayValues = values.filter((item) => Array.isArray(item));
        allStalls = arrayValues.length > 0 ? arrayValues[0] as Stall[] : [];
      }
      
      const stableStalls = allStalls.filter(
        (stall: Stall) => stall.stableId === stable.stableId
      );
      
      console.log(`✅ ${stableStalls.length} baias encontradas para o estábulo ${stable.stableName}`);
      setStalls(stableStalls);
    } catch (error) {
      console.error("Erro ao carregar baias:", error);
      setStalls([]);
    } finally {
      setLoadingStalls(false);
    }
  }, [finalTenantId]);

  const handleDelete = useCallback(async (stableId: number) => {
    if (!finalTenantId) return;
    
    if (!window.confirm(t("stables.confirmDelete"))) {
      return
    }

    try {
      await api.delete(`/stable/${finalTenantId}/${stableId}`)
      console.log("Estábulo excluído com sucesso")
      
      if (openDetailsDialog) {
        setOpenDetailsDialog(false)
      }
      
      setRefreshTrigger((prev) => prev + 1)
    } catch (err) {
      console.error("Erro ao excluir estábulo:", err)
      alert(t("stables.errorDelete"))
    }
  }, [finalTenantId, openDetailsDialog]);

  const columns = useMemo(() => [
    {
      id: "stableName",
      label: t("fields.name"),
      minWidth: 170,
    },
    {
      id: "stallCount",
      label: t("stables.stallsColumn"),
      minWidth: 100,
      align: "center" as const,
      mobileHideLabel: true,
      format: (value: number, row: Stable) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {!stallCountsLoaded ? (
            <CircularProgress size={20} />
          ) : (
            <Chip
              label={stallCounts[row.stableId] || 0}
              size="small"
              color="primary"
              variant="outlined"
              icon={<HomeIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => handleShowStalls(row, e)}
              sx={{ cursor: 'pointer' }}
            />
          )}
        </Box>
      ),
    },
  ], [handleShowStalls, isMobile, stallCounts, stallCountsLoaded]);

  const handleBackHome = () => {
    navigateToPage('dashboard')
  }

  const transformData = useCallback((data: any): any[] => {
    console.log("🔄 transformData executando com stallCounts:", stallCounts);
    
    let stables: any[] = []

    if (Array.isArray(data)) {
      stables = data
    } else if (data && data.stables && Array.isArray(data.stables)) {
      stables = data.stables
    } else if (data && typeof data === "object") {
      try {
        const values = Object.values(data)
        const arrayValues = values.filter((item) => Array.isArray(item))
        stables = arrayValues.length > 0 ? arrayValues[0] : []
      } catch (error) {
        console.error("Erro ao extrair estábulos:", error)
        stables = []
      }
    }

    const stablesWithCount = stables.map(stable => {
      const count = stallCounts[stable.stableId] || 0;
      console.log(`Estábulo ${stable.stableName} (ID: ${stable.stableId}) tem ${count} baias`);
      
      return {
        ...stable,
        stallCount: count
      };
    });

    console.log(`✅ ${stablesWithCount.length} estábulos transformados com contagens`);
    return stablesWithCount;
  }, [stallCounts]);

  const handleNavigateToStalls = useCallback(() => {
    if (selectedStable && finalTenantId) {
      setOpenStallsDialog(false);
      navigateToPage('baias', { 
        mode: 'list', 
        stableId: String(selectedStable.stableId),
        tenantId: String(finalTenantId)
      });
    }
  }, [selectedStable, navigateToPage, finalTenantId]);

  // Se não tem tenantId, mostrar seletor
  if (showTenantSelector) {
    return (
      <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t("stables.selectOwnerTitle")}
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("stables.selectOwnerMessage")}
        </Alert>
        
        {loadingTenants ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t("fields.owner")}</InputLabel>
              <Select
                value={selectedTenantId || ''}
                label={t("fields.owner")}
                onChange={(e) => setSelectedTenantId(Number(e.target.value))}
              >
                {availableTenants.map((tenant) => (
                  <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                    {tenant.tenantName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={handleBackHome}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmTenant}
                disabled={!selectedTenantId}
              >
                {t("common.confirm")}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    );
  }

  // Não renderizar EntityList se não tem tenantId
  if (!finalTenantId) {
    return null;
  }

  return (
    <>
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: isMobile ? "100%" : "auto" }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ flexWrap: "wrap" }}>
              {loadingTenant ? (
                <CircularProgress size={20} />
              ) : (
                <Typography
                  color="text.primary"
                  sx={{
                    maxWidth: { xs: "150px", sm: "300px" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {tenant?.tenantName || t("fields.owner")}
                </Typography>
              )}
            </Breadcrumbs>
          </Box>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={handleBackHome}
            size={isMobile ? "small" : "medium"}
            sx={{ mt: isMobile ? 1 : 0 }}
          >
            {t("common.back")}
          </Button>
        </Box>
      </Paper>

      <EntityList
        key={`stable-list-${stallCountsLoaded ? 'loaded' : 'loading'}`}
        title={t("nav.stables")}
        endpoint={`/stable/${finalTenantId}`}
        columns={columns}
        addPath=""
        editPathPrefix=""
        idField="stableId"
        dataPath="stables"
        onRowClick={handleShowDetails}
        refreshTrigger={refreshTrigger}
        transformData={transformData}
        useEncryptedNavigation={true}
        encryptedPageName="estabulos"
        onAddClick={handleNew}
        onEditClick={handleEdit}
        extraParams={{ tenantId: String(finalTenantId) }}
      />

      {/* Diálogo de Detalhes */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        {selectedStable && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: { xs: 2, sm: 3 },
              }}
            >
              <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                {selectedStable.stableName}
              </Typography>
              <IconButton edge="end" color="inherit" onClick={() => setOpenDetailsDialog(false)} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t("stables.infoTitle")}
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography>
                      <strong>ID:</strong> {selectedStable.stableId}
                    </Typography>
                    <Typography>
                      <strong>{t("fields.name")}:</strong> {selectedStable.stableName}
                    </Typography>
                    <Typography>
                      <strong>{t("stables.totalStalls")}</strong> {stallCounts[selectedStable.stableId] || 0}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setOpenDetailsDialog(false)
                  handleShowStalls(selectedStable)
                }}
              >
                {t('stables.viewStalls')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Diálogo de Baias */}
      <Dialog
        open={openStallsDialog}
        onClose={() => setOpenStallsDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        {selectedStable && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: { xs: 2, sm: 3 },
              }}
            >
              <Typography variant="h6">
                {t('stables.stallsOf', { name: selectedStable.stableName })}
              </Typography>
              <IconButton edge="end" color="inherit" onClick={() => setOpenStallsDialog(false)} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
              {loadingStalls ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <HorseLoader />
                </Box>
              ) : stalls.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size={isMobile ? "small" : "medium"}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("stalls.nameLabel")}</TableCell>
                        {!isMobile && <TableCell align="center">{t("fields.horse")}</TableCell>}
                        {!isMobile && <TableCell align="center">{t("fields.status")}</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stalls.map((stall) => (
                        <TableRow key={stall.stallId} hover>
                          <TableCell>{stall.stallName}</TableCell>
                          {!isMobile && (
                            <TableCell align="center">
                              {stall.horseName || "-"}
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell align="center">
                              <Chip
                                label={stall.horseName ? t("common.occupied") : t("common.available")}
                                color={stall.horseName ? "primary" : "default"}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
                  {t("stables.noStallsFound")}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Button
                onClick={() => setOpenStallsDialog(false)}
                variant="outlined"
              >
                {t("common.close")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNavigateToStalls}
              >
                {t('stables.manageStalls')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}

export default StableList